import * as React from 'react';

import { logEvent } from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Portal } from '@gitbook/spine-react';
import { usePrevious } from '@gitbook/util-react-hooks';

import { useIsOnline } from '../utils';

/**
 * Render an overlay on top of the application when network is offline to prevent interactions with the content.
 */
export function OfflineOverlay() {
    const isOnline = useIsOnline();
    const previousIsOnline = usePrevious(isOnline);
    const offlineTimeRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (!isOnline) {
            offlineTimeRef.current = Date.now();
        }
    }, [isOnline]);

    React.useEffect(() => {
        // Back online
        if (isOnline && previousIsOnline === false && offlineTimeRef.current) {
            const offlineDuration = (Date.now() - offlineTimeRef.current) / 1000;
            // Track an even when the user is back online to analyse how much
            logEvent({
                type: 'app_back_online',
                offlineDuration,
            });
        }
    }, [isOnline, previousIsOnline]);

    if (isOnline) {
        return null;
    }

    return (
        <Portal>
            <div className="absolute inset-0 z-[100] flex size-full flex-1 items-center justify-center bg-overlay text-white">
                <div>
                    <div className="mb-4 flex items-center gap-4">
                        <Icon.CloudOff size={18} />
                        <h2 className="text-ui-heading-small">Connection lost</h2>
                    </div>
                    <div className="mb-4">
                        Your device might be offline, or GitBook might be experiencing issues.
                    </div>
                </div>
            </div>
        </Portal>
    );
}
